export enum ErrorCode {
    ForbiddenError = 1,
    NotFoundError = 2,
    BadRequestError = 3,
    AccessTokenError = 4,
    MissingValueError = 5,
    ValidationError = 6,
    UnsupportedOperationError = 7,
    MaxIterationsError = 8,
    ExternalServiceError = 9,
    AccessTokenLocationError = 10,
    AccessTokenClientError = 11,
    AccessTokenAgencyError = 12,
    AccessTokenAccountError = 13,
    InvalidInputError = 20,
    ParsingError = 21,
    NoStatsDataError = 30,
    ServiceCredentialsMissingError = 31,
    ServiceConfigurationError = 32,
    ServiceAuthenticationError = 33,
    ServiceRateLimitError = 34,
    ServiceAccessError = 35,
    ServiceDisabledForClientError = 36,
    OAuthError = 40,
    TokenPaymentError = 50,
    FinaliseSignupError = 60,
    FinaliseBillingDetailsUpdateError = 61,
    AgencyNotConfiguredToReceiveGoogleAdWordsMarketingRequestsError = 62,
    NotImplementedError = 70,
    GenerateMapError = 80,
    UnsupportedCurrencyError = 90,
    LoginTokenError = 110,
    RequestTokenError = 111,
    AccountAlreadyExistsError = 120,
    NoNetworkConnectionError = 130,
    BatchingError = 140,
    MultipleErrorWrapper = 1000,

    FacebookUnsupportedGetRequestError = 100100,
    FacebookChangedPasswordError = 100200,
    FacebookRateLimitError = 100201,
    FacebookApplicationRateLimitError = 100202,
    FacebookPageRateLimitError = 100203,
    FacebookUnknownFieldsError = 100204,
    FacebookNoAccessTokenError = 100205,
    FacebookPageIdMigratedError = 100206,
    FacebookBlockingCheckpointError = 100207,
    FacebookAPIError = 100208,
    FacebookSuspectedSpamError = 100209,
    FacebookTooManyHashtagsError = 100210,
    FacebookSomeAliasesDoNotExistError = 100211,
    FacebookAppNotInstalledError = 100458,
    FacebookUserCheckpointedError = 100459,
    FacebookAccessTokenExpiredError = 100463,
    FacebookUnconfirmedUserError = 100464,
    FacebookInvalidAccessTokenError = 100467,
    FacebookInvalidSessionError = 100492,
    FacebookDuplicatePostError = 100506,
    FacebookUnknownError = 100300,
    FacebookTimeoutError = 110001,
    FacebookMarketingPermissionsNotGranted = 101000,

    /** Google Codes */
    GoogleAdsUnknownError = 200001,
    GoogleOAuthInvalidGrantError = 200100,
    GoogleAdWordsNotAdsUserError = 200200,
    GoogleAdsPermissionsError = 200201,
    GoogleAdsLocationReportPermissionsError = 200202,

    /** Instagram Codes */
    InstagramMediaTimeoutError = 300100,
    InstagramMediaNotFoundError = 300101,
    InstagramMediaFetchError = 300102,
    InstagramMediaPublishNotReadyError = 9007,
    InstagramServerError = 300200,
    InstagramSuspectedSpamError = 300300,
    InstagramDailyPublishLimitReachedError = 300400,
    InstagramImageTooLargeError = 300500,
    InstagramUnsupportedImageFormatError = 300501,
    InstagramUnsupportedImageRatioError = 300502,
    InstagramUnsupportedVideoFormatError = 300510,
    InstagramRestrictedAccountError = 300600,
    InstagramPublishArgumentsError = 300700,
    InstagramCaptionLengthError = 300710,
    InstagramUnknownError = 310000,

    /** LinkedIn Codes */
    LinkedInCaptionLengthError = 400100,
    LinkedInUnknownError = 410000,
}

export function isErrorByCode<TError>(code: ErrorCode, error: any): error is TError {
    return !!error && error.code === code;
}

export function isAnyErrorByCode<TErrors>(codes: ErrorCode[], error: any): error is TErrors {
    return !!error && codes.includes(error.code);
}

export function errorPredicateByCode<TError>(code: ErrorCode): (error: any) => error is TError {
    return (error: any): error is TError => isErrorByCode(code, error);
}

type ServerErrorReason = ServerValidationError | string | null | undefined;

export interface ServerError {
    code?: number | null;
    name?: string | null;
    reason?: ServerErrorReason;
}

/**
 * This is duplicate of the JoiValidationError interface. Duplicated here so we don't need to import Joi in all
 * dependent projects.
 */
export interface ServerValidationError {
    isJoi: boolean;
    details: ServerValidationErrorItem[];
    _object: any;
}

export interface ServerValidationErrorItem {
    message: string;
    type: string;
    path: string;
    options?: ServerValidationOptions;
    context?: {
        [key: string]: any;
        value?: any;
    };
}

/**
 * Refer to the Joi documentation for details on these properties.
 */
export interface ServerValidationOptions {
    abortEarly?: boolean;
    convert?: boolean;
    allowUnknown?: boolean;
    skipFunctions?: boolean;
    stripUnknown?: boolean | { arrays: boolean } | { arrays: boolean; objects: boolean } | { objects: boolean };
    language?: Record<string, any>;
    presence?: string;
    context?: Record<string, any>;
    noDefaults?: boolean;
}

export function isServerValidationError(error: any): error is ServerValidationError {
    return error && (error.code === ErrorCode.ValidationError || error.isJoi);
}

export function coerceServerErrorReasonToString(reason: ServerErrorReason): string {
    if (reason) {
        if (isServerValidationError(reason)) {
            // TODO: better output of validation messages, i18n.
            const msgs = [];
            for (const entry of reason.details) {
                msgs.push(entry.message);
            }
            return `${msgs.join('; ')}`;
        } else {
            return reason;
        }
    } else {
        return '';
    }
}

/**
 * Re-exported these for legacy purposes
 */
export { assertDefined, assertNever } from '@deltasierra/utilities-assertions';

/**
 * You might want to use this type for Promise rejections.
 * Ideally any should be avoided, but JS errors can actually be any type,
 * so we want to allow any in those circumstances.
 * This it can be useful to document that this is specifically an "any error" type, and not just
 * "I don't know what this is and can't work it out but I need to get this out yesterday so I'm going to put 'any'
 */
export type AnyError = any;
